import { useUploadStore } from "../../store/uploadStore";
import { ProgressBar } from "../ui/ProgressBar";

// Active statuses float to top, pending in middle, done/error sink to bottom (auto-removed in 1s)
const STATUS_ORDER: Record<string, number> = {
  uploading: 0,
  hashing: 0,
  completing: 0,
  pending: 1,
  done: 2,
  error: 2,
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Queued",
  hashing: "Hashing...",
  uploading: "Uploading",
  completing: "Finalizing...",
  done: "Done",
  error: "Failed",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "text-gray-500",
  hashing: "text-blue-400",
  uploading: "text-brand-400",
  completing: "text-yellow-400",
  done: "text-green-400",
  error: "text-red-400",
};

export function UploadQueue() {
  const { queue, isVisible, completedCount, failedCount, reset, setVisible } = useUploadStore();

  if (!isVisible) return null;

  const items = Array.from(queue.values());

  const activeCount = items.filter(
    (i) => i.status !== "done" && i.status !== "error" && i.status !== "pending"
  ).length;

  const isUploading = activeCount > 0 || items.some((i) => i.status === "pending");

  // Sort: active (uploading/hashing/completing) → pending → done/error
  const sorted = [...items].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 1) - (STATUS_ORDER[b.status] ?? 1)
  );

  const displayItems = sorted.slice(0, 20);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 rounded-none border-t border-gray-700 bg-gray-900 shadow-2xl sm:bottom-4 sm:left-auto sm:right-4 sm:w-80 sm:rounded-xl sm:border">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-200">
            {isUploading
              ? `${items.filter((i) => i.status === "pending").length + activeCount} files remaining…`
              : "Upload complete"}
          </p>
          <p className="text-xs text-gray-500">
            {completedCount > 0 && `${completedCount} completed`}
            {completedCount > 0 && failedCount > 0 && " · "}
            {failedCount > 0 && (
              <span className="text-red-400">{failedCount} failed</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isUploading && (
            <button
              onClick={reset}
              className="text-xs text-gray-400 transition-colors hover:text-gray-200"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => setVisible(false)}
            className="text-gray-500 transition-colors hover:text-gray-300"
            aria-label="Minimize"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 8a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7A.5.5 0 014 8z" />
            </svg>
          </button>
        </div>
      </div>

      {/* File list */}
      {queue.size > 0 && (
        <div className="max-h-60 overflow-y-auto">
          {sorted.length > 20 && (
            <p className="px-4 py-1.5 text-center text-[11px] text-gray-600">
              Showing {displayItems.length} of {sorted.length} files
            </p>
          )}
          {displayItems.map((item) => (
            <div key={item.id} className="border-b border-gray-800 px-4 py-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="truncate text-xs text-gray-300" title={item.relativePath}>
                  {item.uploadAs}
                </span>
                <span
                  className={`ml-2 flex-shrink-0 text-xs ${STATUS_COLORS[item.status] ?? "text-gray-400"}`}
                >
                  {STATUS_LABELS[item.status] ?? item.status}
                </span>
              </div>
              {item.status === "uploading" && <ProgressBar value={item.progress} />}
              {item.status === "error" && item.error && (
                <p className="text-xs text-red-400">{item.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
